/**
 * SButton 색상/사이즈 설정 — sd-button(component.button 토큰) 충실 포팅.
 * Stencil `name`(예: primary_sm)의 preset을 color + outline(boolean) + size 로 분리.
 *  - primary / danger : solid·outline 모두 지원
 *  - secondary        : solid 전용 (outline 스타일 없음 → outline 무시)
 *  - neutral          : 흰 배경 고정, outline 은 회색 테두리만 추가(solid = 테두리 없는 흰 버튼)
 * 색상은 theme.css의 `--cmp-button-*` CSS 변수를 참조한다.
 */
export const BUTTON_COLORS = ['primary', 'secondary', 'neutral', 'danger'] as const;
export const BUTTON_SIZES = ['xs', 'sm', 'md', 'lg'] as const;

export type SButtonColor = (typeof BUTTON_COLORS)[number];
export type SButtonSize = (typeof BUTTON_SIZES)[number];

interface PresetColors {
  bg: string;
  bgHover: string;
  text: string;
  icon: string;
  border: string;
}

const V = (path: string) => `var(--cmp-button-${path})`;

/**
 * (color, outline) → 색상 프리셋.
 * secondary 는 outline 을 지원하지 않으므로 outline 인자를 무시하고 항상 solid 를 돌려준다.
 */
export function buttonPreset(color: SButtonColor, outline: boolean): PresetColors {
  switch (color) {
    case 'primary':
      return outline
        ? {
            bg: V('brand-outline-bg-default'),
            bgHover: V('brand-outline-bg-hover'),
            text: V('brand-outline-content'),
            icon: V('brand-outline-content'),
            border: V('brand-outline-border'),
          }
        : {
            bg: V('brand-strong-bg-default'),
            bgHover: V('brand-strong-bg-hover'),
            text: V('brand-strong-content'),
            icon: V('brand-strong-content'),
            border: 'transparent',
          };
    case 'secondary':
      return {
        bg: V('brand-subtle-bg-default'),
        bgHover: V('brand-subtle-bg-hover'),
        text: V('brand-subtle-content'),
        icon: V('brand-subtle-content'),
        border: 'transparent',
      };
    case 'neutral':
      // solid·outline 모두 흰 배경 + 동일 텍스트, 테두리 유무만 차이
      return {
        bg: V('neutral-outline-bg-default'),
        bgHover: V('neutral-outline-bg-hover'),
        text: V('neutral-outline-text'),
        icon: V('neutral-outline-icon'),
        border: outline ? V('neutral-outline-border') : 'transparent',
      };
    case 'danger':
      return outline
        ? {
            bg: V('danger-outline-bg-default'),
            bgHover: V('danger-outline-bg-hover'),
            text: V('danger-outline-content'),
            icon: V('danger-outline-content'),
            border: V('danger-outline-border'),
          }
        : {
            bg: V('danger-strong-bg-default'),
            bgHover: V('danger-strong-bg-hover'),
            text: V('danger-strong-content'),
            icon: V('danger-strong-content'),
            border: 'transparent',
          };
  }
}

export const BUTTON_SIZE_CONFIG: Record<
  SButtonSize,
  {
    height: string;
    paddingX: string;
    gap: string;
    iconSize: number;
    fontSize: number;
    fontWeight: number;
    radius: string;
    minWidth: number;
  }
> = {
  xs: {
    height: 'var(--cmp-button-xs-height)',
    paddingX: 'var(--cmp-button-xs-paddingX)',
    gap: 'var(--cmp-button-xs-gap)',
    iconSize: 12,
    fontSize: 12,
    fontWeight: 500,
    radius: 'var(--cmp-button-radius-sm)',
    minWidth: 36,
  },
  sm: {
    height: 'var(--cmp-button-sm-height)',
    paddingX: 'var(--cmp-button-sm-paddingX)',
    gap: 'var(--cmp-button-sm-gap)',
    iconSize: 16,
    fontSize: 12,
    fontWeight: 500,
    radius: 'var(--cmp-button-radius-sm)',
    minWidth: 70,
  },
  md: {
    height: 'var(--cmp-button-md-height)',
    paddingX: 'var(--cmp-button-md-paddingX)',
    gap: 'var(--cmp-button-md-gap)',
    iconSize: 20,
    fontSize: 16,
    fontWeight: 500,
    radius: 'var(--cmp-button-radius-md)',
    minWidth: 100,
  },
  lg: {
    height: 'var(--cmp-button-lg-height)',
    paddingX: 'var(--cmp-button-lg-paddingX)',
    gap: 'var(--cmp-button-lg-gap)',
    iconSize: 24,
    fontSize: 18,
    fontWeight: 500,
    radius: 'var(--cmp-button-radius-md)',
    minWidth: 120,
  },
};

export const BUTTON_FOCUS_RING = 'var(--sys-color-border-accent)';
export const BUTTON_DISABLED_BG = 'var(--cmp-button-bg-disabled, #E1E1E1)';
export const BUTTON_DISABLED_BORDER = 'var(--cmp-button-border-disabled, #CCCCCC)';
export const BUTTON_DISABLED_TEXT = 'var(--cmp-button-text-disabled, #888888)';
export const BUTTON_DISABLED_ICON = 'var(--cmp-button-icon-disabled, #BBBBBB)';
